/* eslint-disable @typescript-eslint/no-explicit-any */
import type { PropertyData, FormData as ProductFormData } from '../../AddProductPage/types'
import type { Product } from '../../../features/products/types'
import { serializeProductMedia } from '../../AddProductPage/utils/serializeMedia'
import { buildSaleModePayload } from '../../AddProductPage/utils/saleMode'
import { buildTaxonomyPayload } from '../../AddProductPage/utils/taxonomy'
import {
  optionalIdField,
  requiredId,
} from '../../../features/products/utils/productPayload'
import { parseStockUpTrigger } from '../../../features/products/utils/stockStatus'

const generateUniqueId = () => {
  return Math.random().toString(36).substr(2, 9)
}

export const transformToUpdateFormat = (
  formData: ProductFormData,
  propertyData: PropertyData,
  _originalProduct: Product,
  materials: any[],
  colors: any[],
  status?: 'DRAFT' | 'PUBLISHED'
): FormData => {
  const multipartFormData = new FormData()
  // PE-2169 — one product-level threshold, stamped onto every leaf.
  const stockUpTrigger = parseStockUpTrigger(formData.stockUpTrigger)
  const imageNames: string[] = []

  const sizes = propertyData.sizes.map((sizeData) => ({
    sizeId: sizeData.sizeId,
    additionalPrice: sizeData.price,
    additionalCostPrice: sizeData.additionalCostPrice,
    materials: sizeData.materials.map((materialData) => ({
      materialId: requiredId(
        materials.find((m) => m.name === materialData.name)?.id,
        'material',
        materialData.name,
      ),
      additionalPrice: materialData.price,
      additionalCostPrice: materialData.additionalCostPrice,
      colours: materialData.colors.map((colorData) => {
        const colorName =
          colors.find((c) => c.hexValue === colorData.hex)?.name || "unknown";

        // Generate unique names for NEW images and add to FormData
        const colorImageNames =
          colorData.images?.map((image) => {
            const uniqueId = generateUniqueId();
            const imageName = `color-${colorName}-${uniqueId}`;
            multipartFormData.append("files", image, imageName);
            return imageName;
          }) || [];

        imageNames.push(...colorImageNames);

        // Combine existing imageUrls with new image names
        const allImageUrls = [
          ...(colorData.imageUrls || []),
          ...colorImageNames,
        ];

        return {
          colourId: requiredId(
            colors.find((c) => c.hexValue === colorData.hex)?.id ||
              colorData.id,
            'colour',
            colorData.hex,
          ),
          additionalPrice: colorData.price,
          additionalCostPrice: colorData.additionalCostPrice,
          quantity: colorData.quantity,
          weight: colorData.weight,
          imageUrls: allImageUrls,
          stockUpTrigger,
        };
      }),
    })),
  }));

  // PE-1283 §7 E4 — replace the typed media gallery only when the form carries
  // media items. An empty/undefined gallery omits the key so existing products
  // (image-only, no gallery interaction) are untouched on the BFF.
  const media = serializeProductMedia(formData.media)

  // PE-2087 — many-to-many taxonomy links. ⚠️ TWO NAMESPACES: `categoryId` below is
  // rack-api's OWN legacy Category table (required scalar); `collectionIds`/`categoryIds`/
  // `occasionIds` are product-service ids applied via setTaxonomy. No id overlap — never
  // merge them. UpdateProductDto is PartialType(CreateProductDto) so it accepts all three,
  // and rack-api's updateDirect forwards them to setTaxonomy. Empty selections omit their
  // key, which leaves the product's current links untouched (sending [] would clear them).
  // See AddProductPage/utils/taxonomy.ts.
  const taxonomy = buildTaxonomyPayload(formData)

  const productData =
    status === "DRAFT"
      ? {
          name: formData.name,
          description: formData.details,
          categoryId: formData.category,
          ...optionalIdField('sizeGuideId', formData.styleGuide),
          productionTimeInDays: parseInt(formData.productionTimeInDays) || 0,
          ...buildSaleModePayload(formData),
          sizes,
          ...(media.length > 0 ? { media } : {}),
          ...taxonomy,
        }
      : {
          name: formData.name,
          description: formData.details,
          categoryId: formData.category,
          ...optionalIdField('sizeGuideId', formData.styleGuide),
          productionTimeInDays: parseInt(formData.productionTimeInDays) || 0,
          ...buildSaleModePayload(formData),
          status,
          sizes,
          ...(media.length > 0 ? { media } : {}),
          ...taxonomy,
        };

  multipartFormData.append('productData', JSON.stringify(productData))
  
  return multipartFormData
}